'use client'

import { useState } from 'react'
import { X, Sparkles, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react'
import Link from 'next/link'
import type { Announcement, AnnouncementType } from '@/lib/config/announcements'

interface AnnouncementBannerProps {
  announcement: Announcement
  onDismiss: (id: string) => void
}

const TYPE_CONFIG: Record<AnnouncementType, { bg: string; text: string; icon: typeof Sparkles }> = {
  feature:  { bg: 'bg-[var(--color-brand-indigo)]', text: 'text-white', icon: Sparkles },
  warning:  { bg: 'bg-[var(--color-status-warning)]', text: 'text-[var(--color-bg-page)]', icon: AlertTriangle },
  problem:  { bg: 'bg-[var(--color-status-error)]', text: 'text-white', icon: AlertCircle },
  resolved: { bg: 'bg-[var(--color-status-success)]', text: 'text-white', icon: CheckCircle },
}

export function AnnouncementBanner({ announcement, onDismiss }: AnnouncementBannerProps) {
  const [visible, setVisible] = useState(true)

  if (!visible) return null

  const config = TYPE_CONFIG[announcement.type] ?? TYPE_CONFIG.feature
  const Icon = config.icon

  const handleDismiss = () => {
    setVisible(false)
    onDismiss(announcement.id)
  }

  return (
    <div
      className={`${config.bg} ${config.text} relative w-full px-4 py-2.5 transition-all duration-300`}
      role="status"
      aria-label="Announcement"
      data-testid="announcement-banner"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
          <span className="font-semibold">{announcement.title}</span>
          <span className="hidden truncate sm:inline">{announcement.body}</span>
          {announcement.href && (
            <Link
              href={announcement.href}
              // Sitewide promo banner: nofollow so the same CTA on every
              // public page doesn't advertise a redirect (announcements often
              // point at auth-gated routes like /feed) — Ahrefs "links to
              // redirect" 2026-09-14.
              rel="nofollow"
              className="shrink-0 underline underline-offset-2 hover:no-underline"
            >
              Learn more
            </Link>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full opacity-80 transition-opacity hover:opacity-100"
          aria-label="Dismiss announcement"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
